"use client";

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Star, Loader2, PenLine, CheckCircle2, ImagePlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";

/**
 * Customer review submission form on the PDP (WC7). Submits (multipart) to
 * POST /api/products/[id]/reviews, which stores the review + photos UNPUBLISHED
 * for admin moderation — nothing appears publicly until approved.
 *
 * Rendered as a modal Dialog (not an inline expanding form) so opening it never
 * reflows the surrounding page. Customers can attach up to 3 photos.
 */

// Keep in sync with the server (lib/product-reviews MAX_REVIEW_IMAGES + the
// route's MAX_IMAGE_BYTES / ALLOWED_IMAGE_TYPES). Defined locally so this
// client component doesn't import the server-only reviews module.
const MAX_IMAGES = 3;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];

interface PickedImage {
  file: File;
  url: string;
}

export function ProductReviewForm({ productId }: { productId: string }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [images, setImages] = useState<PickedImage[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Revoke any outstanding object URLs when the component unmounts. A ref keeps
  // the cleanup pointed at the latest list without re-running the effect.
  // The ref is synced in an effect (not during render) per react-hooks/refs.
  const imagesRef = useRef<PickedImage[]>([]);
  useEffect(() => {
    imagesRef.current = images;
  }, [images]);
  useEffect(() => {
    return () => {
      imagesRef.current.forEach((img) => URL.revokeObjectURL(img.url));
    };
  }, []);

  // Deep-link from the order page's "Review this bouquet" link
  // (/p/<slug>?review=1#reviews): jump to the reviews section and pop the form
  // open automatically, so the customer never has to hunt for it. Runs once on
  // mount; history is mutated here (an effect, not during render) per the
  // "no router update while rendering" rule.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const wantsReview =
      params.get("review") === "1" || window.location.hash === "#write-review";
    if (!wantsReview) return;
    // Belt-and-braces alongside the native #reviews hash scroll — instant (not
    // smooth) so we settle on the section before the modal's scroll-lock kicks in.
    document.getElementById("reviews")?.scrollIntoView({ block: "start" });
    // eslint-disable-next-line react-hooks/set-state-in-effect -- an explicit review deep link must open the modal once browser URL state is available.
    setOpen(true);
    // Strip ?review=1 so a refresh / shared URL doesn't re-pop the modal; keep
    // the #reviews anchor so the section stays in view behind it.
    params.delete("review");
    const qs = params.toString();
    window.history.replaceState(
      null,
      "",
      window.location.pathname + (qs ? `?${qs}` : "") + "#reviews",
    );
  }, []);

  const addFiles = (fileList: FileList | null) => {
    if (!fileList) return;
    const remaining = MAX_IMAGES - images.length;
    if (remaining <= 0) {
      toast.error(`You can attach up to ${MAX_IMAGES} photos.`);
      return;
    }
    const incoming = Array.from(fileList);
    const accepted: PickedImage[] = [];
    for (const file of incoming) {
      if (accepted.length >= remaining) break;
      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast.error("Photos must be JPEG, PNG, or WebP.");
        continue;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        toast.error("Each photo must be under 5MB.");
        continue;
      }
      accepted.push({ file, url: URL.createObjectURL(file) });
    }
    if (incoming.length > remaining) {
      toast.error(`You can attach up to ${MAX_IMAGES} photos.`);
    }
    if (accepted.length) setImages((prev) => [...prev, ...accepted]);
  };

  const removeImage = (idx: number) => {
    setImages((prev) => {
      const next = [...prev];
      const [removed] = next.splice(idx, 1);
      if (removed) URL.revokeObjectURL(removed.url);
      return next;
    });
  };

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    const form = e.currentTarget;
    const data = new FormData(form);
    const author = String(data.get("author") ?? "").trim();
    const reviewBody = String(data.get("body") ?? "").trim();

    if (!author) {
      toast.error("Please add your name.");
      return;
    }
    if (rating < 1) {
      toast.error("Please choose a star rating.");
      return;
    }
    if (!reviewBody) {
      toast.error("Please write a few words.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = new FormData();
      payload.append("author", author);
      payload.append("rating", String(rating));
      payload.append("body", reviewBody);
      for (const img of images) payload.append("images", img.file);

      // No explicit Content-Type — the browser sets multipart/form-data with
      // the correct boundary.
      const res = await fetch(`/api/products/${encodeURIComponent(productId)}/reviews`, {
        method: "POST",
        body: payload,
      });
      const json = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        toast.error(json.error ?? "Couldn't submit your review.");
        return;
      }
      images.forEach((img) => URL.revokeObjectURL(img.url));
      setImages([]);
      setOpen(false);
      setDone(true);
      toast.success("Thank you for your review!");
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-foreground">
        <CheckCircle2 className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
        Thank you! Your review will appear once we&apos;ve approved it.
      </div>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Reset transient state when the modal closes without submitting.
        if (!next) {
          setRating(0);
          setHover(0);
          setImages((prev) => {
            prev.forEach((img) => URL.revokeObjectURL(img.url));
            return [];
          });
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="min-h-11 sm:min-h-0">
          <PenLine className="h-4 w-4" /> Write a review
        </Button>
      </DialogTrigger>

      {/* DIALOGVH: `dvh`, never `vh`. `vh` is the LARGE viewport, so on a phone
          with the URL bar showing a 90vh dialog runs past the visible area and
          the "Submit review" button sits under the browser chrome, with no page
          scroll to recover it (the dialog is position:fixed). */}
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="heading-card">Write a review</DialogTitle>
          <DialogDescription>
            Reviews are checked before they appear — we only publish genuine feedback.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4" aria-label="Write a review">
          <div>
            <Label htmlFor="review-author">
              Your name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="review-author"
              name="author"
              required
              maxLength={120}
              className="mt-1.5 min-h-11"
              placeholder="e.g. Sarah M."
            />
          </div>

          <div>
            <span className="text-sm font-medium">
              Rating <span className="text-destructive">*</span>
            </span>
            <div className="mt-1.5 flex gap-1" role="radiogroup" aria-label="Star rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={rating === n}
                  aria-label={`${n} star${n === 1 ? "" : "s"}`}
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  className="inline-flex h-11 w-11 items-center justify-center"
                >
                  <Star
                    className={`h-7 w-7 ${(hover || rating) >= n ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`}
                  />
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="review-body">
              Your review <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="review-body"
              name="body"
              required
              rows={4}
              maxLength={2000}
              className="mt-1.5"
              placeholder="What did you love about it?"
            />
          </div>

          <div>
            <span className="text-sm font-medium">
              Photos{" "}
              <span className="font-normal text-muted-foreground">(optional, up to {MAX_IMAGES})</span>
            </span>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {images.map((img, i) => (
                <div
                  key={img.url}
                  className="relative h-20 w-20 overflow-hidden rounded-md border border-border bg-muted"
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={img.url} alt={`Selected photo ${i + 1}`} className="h-full w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImage(i)}
                    aria-label={`Remove photo ${i + 1}`}
                    className="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {images.length < MAX_IMAGES && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                >
                  <ImagePlus className="h-5 w-5" />
                  <span className="text-[10px] leading-none">Add photo</span>
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="sr-only"
              tabIndex={-1}
              aria-hidden="true"
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = ""; // allow re-picking the same file
              }}
            />
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
                </>
              ) : (
                "Submit review"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
